'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useRouter, usePathname } from 'next/navigation'
import { User, LogOut, Settings, Edit, Sun, Moon, LayoutDashboard, Users } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useScrollDirection } from '@/hooks/useScrollDirection'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { useTheme } from '@/contexts/ThemeContext'
import { LazyComponents } from '@/components/LazyComponents'
import { createPrefetchHandler } from '@/lib/chat-prefetch'
import { parseAuthToken } from '@/utils/base64'
import { useGlobalUnreadStore } from '@/store/chatStore'

interface UserProfile {
  name: string
  profile_image?: string
  certification_level?: string
  is_premium?: boolean
  id?: string
}

export default function Header() {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  const [user, setUser] = useState<UserProfile | null>(null)
  const [isInstructor, setIsInstructor] = useState(false)
  const router = useRouter()
  const pathname = usePathname()
  const { scrollDirection, scrollY } = useScrollDirection()
  const { theme, toggleTheme } = useTheme()

  const isHeaderHidden = scrollDirection === 'down' && scrollY > 80

  useEffect(() => {
    if (typeof window === 'undefined') return
    const headerEl = document.getElementById('main-header-wrapper')
    const h = headerEl ? headerEl.offsetHeight : 56
    document.documentElement.style.setProperty(
      '--navbar-offset',
      isHeaderHidden ? '0px' : `${h}px`
    )
  }, [isHeaderHidden])

  const { totalUnread: unreadChatCount, fetchUnreadCount } = useGlobalUnreadStore()

  const isChatRoom = pathname?.match(/^\/chat\/[^/]+$/)

  useEffect(() => {
    // OAuth 콜백 후 URL에 auth_token이 있으면 localStorage에 저장 (기존 유저 로그인 복구)
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      const urlAuthToken = params.get('auth_token')
      const urlSupabaseToken = params.get('supabase_token')
      if (urlAuthToken) {
        localStorage.setItem('auth_token', urlAuthToken)
        if (urlSupabaseToken) {
          localStorage.setItem('supabase_token', urlSupabaseToken)
        }
        // URL에서 토큰 파라미터 제거 (깔끔한 URL 유지)
        params.delete('auth_token')
        params.delete('supabase_token')
        const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '')
        window.history.replaceState({}, '', newUrl)
        window.dispatchEvent(new Event('authStateChanged'))
      }
    }
  }, [])

  useEffect(() => {
    if (isChatRoom) return

    const loadUserFromLocalStorage = async () => {
      const token = localStorage.getItem('auth_token')

      if (token) {
        try {
          const decoded = parseAuthToken(token)

          const supabase = getSupabaseClient()

          let query = supabase
            .from('user_profiles')
            .select('*')

          if (decoded.kakao_id) {
            query = query.eq('kakao_id', decoded.kakao_id)
          } else if (decoded.naver_id) {
            query = query.eq('naver_id', decoded.naver_id)
          } else {
            console.error('No kakao_id or naver_id in token')
            localStorage.removeItem('auth_token')
            setUser(null)
            return
          }

          const { data: profile, error } = await query.single()

          if (error || !profile) {
            if (decoded.profile_completed === false) {
              setUser({ name: decoded.name, profile_image: undefined, certification_level: undefined, is_premium: false })
              return
            }
            localStorage.removeItem('auth_token')
            setUser(null)
            return
          }

          setUser({
            id: profile.id,
            name: profile.name,
            profile_image: profile.profile_image,
            certification_level: profile.certification_level,
            is_premium: profile.is_premium
          })

          if (profile.id) {
            const { data: instructorData } = await supabase
              .from('instructor_profiles')
              .select('id')
              .eq('user_id', profile.id)
              .single()
            setIsInstructor(!!instructorData)
          }
        } catch (error) {
          console.error('Failed to load user profile:', error)
          localStorage.removeItem('auth_token')
          setUser(null)
        }
      }
    }

    loadUserFromLocalStorage()

    const handleStorageChange = () => {
      loadUserFromLocalStorage()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('authStateChanged', loadUserFromLocalStorage)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('authStateChanged', loadUserFromLocalStorage)
    }
  }, [isChatRoom])

  useEffect(() => {
    if (isChatRoom) return

    if (user && user.id) {
      const initialTimeout = setTimeout(() => fetchUnreadCount(), 500)
      const interval = setInterval(() => fetchUnreadCount(), 300000)

      let debounceTimer: NodeJS.Timeout | null = null
      const handleChatUpdate = () => {
        if (debounceTimer) clearTimeout(debounceTimer)
        debounceTimer = setTimeout(() => fetchUnreadCount(), 2000)
      }
      window.addEventListener('chatRead', handleChatUpdate)
      window.addEventListener('newChatMessage', handleChatUpdate)

      return () => {
        clearTimeout(initialTimeout)
        clearInterval(interval)
        if (debounceTimer) clearTimeout(debounceTimer)
        window.removeEventListener('chatRead', handleChatUpdate)
        window.removeEventListener('newChatMessage', handleChatUpdate)
      }
    }
  }, [user?.id, fetchUnreadCount, isChatRoom])

  useEffect(() => {
    if (isChatRoom) return

    const handleClickOutside = (event: MouseEvent) => {
      if (isProfileMenuOpen) {
        const target = event.target as HTMLElement
        if (!target.closest('.profile-menu-container')) {
          setIsProfileMenuOpen(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isProfileMenuOpen, isChatRoom])

  if (isChatRoom) return null

  const handleLogout = async () => {
    localStorage.removeItem('auth_token')
    setUser(null)
    window.dispatchEvent(new Event('authStateChanged'))
    router.push('/')
  }

  const navigation = [
    { name: '홈', href: '/' },
    { name: '투어', href: '/tours' },
    { name: '강사찾기', href: '/instructors' },
    { name: '지도', href: '/map' },
    {
      name: '가이드',
      href: '/guide',
      submenu: [
        { name: '입문 가이드', href: '/guide' },
        { name: '안전 가이드', href: '/safety' },
        { name: '장비 가이드', href: '/equipment' },
        { name: '바다 포인트', href: '/map' },
      ]
    },
  ]

  return (
    <div
      id="main-header-wrapper"
      className={cn(
        "fixed top-0 left-0 right-0 z-50 safe-top transition-all duration-300 px-3 pt-2",
        isHeaderHidden ? '-translate-y-full' : 'translate-y-0'
      )}
    >
    <header
      className="backdrop-blur-xl rounded-2xl transition-all duration-300"
      style={{
        background: theme === 'light'
          ? 'linear-gradient(45deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 249, 255, 0.95) 100%)'
          : 'rgba(17, 24, 39, 0.95)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.05)'
      }}
    >
      <div className="max-w-7xl mx-auto px-2 xxs:px-3 xs:px-5 mobile:px-6 sm:px-8">
        <div className="flex justify-between items-center h-12 xxs:h-14 xs:h-16">
          <Link href="/" className="flex items-center flex-shrink-0">
            <img
              src={theme === 'light' ? '/logo.png' : '/logo_dark.png'}
              alt="오늘다이빙"
              className="h-14 xxs:h-16 xs:h-17 w-14 xxs:w-16 xs:w-17 object-contain"
            />
          </Link>

          {/* 데스크톱 네비게이션 */}
          <nav className="hidden md:flex space-x-6 items-center">
            {navigation.map((item) => {
              if (item.submenu) {
                return (
                  <div key={item.name} className="relative group">
                    <Link
                      href={item.href || '/'}
                      className={`relative ${theme === 'light' ? 'text-gray-700 group-hover:text-blue-600' : 'text-gray-300 group-hover:text-blue-400'} transition-colors duration-200 font-medium text-sm flex items-center`}
                    >
                      {item.name} ▾
                    </Link>
                    <div className={`absolute left-0 top-full mt-2 w-48 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 ${
                      theme === 'light'
                        ? 'bg-white border border-gray-200'
                        : 'bg-gray-800 border border-gray-700'
                    }`}>
                      {item.submenu.map((subitem) => (
                        <Link
                          key={subitem.name}
                          href={subitem.href}
                          className={`block px-4 py-2 text-sm transition-colors first:rounded-t-lg last:rounded-b-lg ${
                            theme === 'light'
                              ? 'text-gray-700 hover:bg-gray-100'
                              : 'text-gray-300 hover:bg-gray-700'
                          }`}
                        >
                          <span className="flex items-center justify-between">
                            {subitem.name}
                            {subitem.name === '채팅' && unreadChatCount > 0 && (
                              <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                                {unreadChatCount > 99 ? '99+' : unreadChatCount}
                              </span>
                            )}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )
              }

              const authToken = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
              const prefetchHandler = item.name === '채팅' && user?.id
                ? createPrefetchHandler(authToken, user.id)
                : undefined

              return (
                <Link
                  key={item.name}
                  href={item.href!}
                  onMouseEnter={prefetchHandler}
                  className={`relative ${theme === 'light' ? 'text-gray-700 hover:text-blue-600' : 'text-gray-300 hover:text-blue-400'} transition-colors duration-200 font-medium text-sm`}
                >
                  {item.name}
                </Link>
              )
            })}
          </nav>

          {/* 데스크톱 사용자 메뉴 */}
          <div className="hidden md:flex items-center space-x-3">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all duration-200 ${
                theme === 'light'
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-gray-800 text-yellow-400 hover:bg-gray-700'
              }`}
              aria-label="테마 전환"
            >
              {theme === 'dark' ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </button>

            {user ? (
              <>
                {user.id && <LazyComponents.NotificationCenter userId={user.id} />}
                <div className="relative profile-menu-container">
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className={`flex items-center space-x-2 rounded-lg px-2 py-1 transition-colors duration-200 ${
                      theme === 'light' ? 'hover:bg-gray-200' : 'hover:bg-gray-800'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center"
                      style={{ background: theme === 'dark' ? 'rgba(94,166,255,0.15)' : 'rgba(43,109,240,0.1)' }}>
                      {user.profile_image ? (
                        <img src={user.profile_image} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-4 h-4" style={{ color: theme === 'dark' ? '#5ea6ff' : '#2b6df0' }} />
                      )}
                    </div>
                    <span className={`text-sm font-medium ${theme === 'light' ? 'text-gray-800' : 'text-white'}`}>{user.name}</span>
                    {user.is_premium && (
                      <span className="px-2 py-0.5 text-xs bg-gradient-to-r from-yellow-400 to-orange-400 text-black rounded-full font-bold whitespace-nowrap">
                        프리미엄
                      </span>
                    )}
                  </button>

                  {isProfileMenuOpen && (
                    <div className={`absolute right-0 top-full mt-2 w-48 rounded-lg shadow-xl z-50 ${
                      theme === 'light' ? 'bg-white border border-gray-200' : 'bg-gray-800 border border-gray-700'
                    }`}>
                      <Link
                        href="/profile"
                        className={`p-3 block transition-colors ${
                          theme === 'light' ? 'border-b border-gray-200 hover:bg-gray-50' : 'border-b border-gray-700 hover:bg-gray-700'
                        }`}
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center"
                            style={{ background: theme === 'dark' ? 'rgba(94,166,255,0.15)' : 'rgba(43,109,240,0.1)' }}>
                            {user.profile_image ? (
                              <img src={user.profile_image} alt={user.name} className="w-full h-full object-cover" />
                            ) : (
                              <User className="w-5 h-5" style={{ color: theme === 'dark' ? '#5ea6ff' : '#2b6df0' }} />
                            )}
                          </div>
                          <div>
                            <p className={`text-sm font-medium ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>{user.name}</p>
                            <p className={`text-xs ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>{user.certification_level}</p>
                          </div>
                        </div>
                      </Link>
                      <div className="py-1">
                        <Link href="/profile" className={`flex items-center px-3 py-2 text-sm transition-colors ${theme === 'light' ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 hover:bg-gray-700'}`} onClick={() => setIsProfileMenuOpen(false)}>
                          <User className="w-4 h-4 mr-2" />내 프로필
                        </Link>
                        <Link href="/profile/edit" className={`flex items-center px-3 py-2 text-sm transition-colors ${theme === 'light' ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 hover:bg-gray-700'}`} onClick={() => setIsProfileMenuOpen(false)}>
                          <Edit className="w-4 h-4 mr-2" />프로필 수정
                        </Link>
                        {isInstructor && (
                          <>
                            <Link href="/instructor/dashboard" className={`flex items-center px-3 py-2 text-sm font-medium transition-colors ${theme === 'light' ? 'hover:bg-blue-50' : 'hover:bg-blue-900/20'}`} style={{ color: theme === 'dark' ? '#5ea6ff' : '#2b6df0' }} onClick={() => setIsProfileMenuOpen(false)}>
                              <LayoutDashboard className="w-4 h-4 mr-2" />강사 대시보드
                            </Link>
                            <Link href="/instructors/crm" className={`flex items-center px-3 py-2 text-sm font-medium transition-colors ${theme === 'light' ? 'hover:bg-blue-50' : 'hover:bg-blue-900/20'}`} style={{ color: theme === 'dark' ? '#5ea6ff' : '#2b6df0' }} onClick={() => setIsProfileMenuOpen(false)}>
                              <Users className="w-4 h-4 mr-2" />수강생 관리
                            </Link>
                          </>
                        )}
                        <Link href="/settings" className={`flex items-center px-3 py-2 text-sm transition-colors ${theme === 'light' ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 hover:bg-gray-700'}`} onClick={() => setIsProfileMenuOpen(false)}>
                          <Settings className="w-4 h-4 mr-2" />설정
                        </Link>
                      </div>
                      <div className={`py-1 ${theme === 'light' ? 'border-t border-gray-200' : 'border-t border-gray-700'}`}>
                        <button onClick={() => { setIsProfileMenuOpen(false); handleLogout() }} className="flex items-center w-full px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors">
                          <LogOut className="w-4 h-4 mr-2" />로그아웃
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Link
                href="/login"
                className="text-white px-4 xs:px-6 py-1.5 xs:py-2 rounded-full font-medium text-xs xs:text-sm whitespace-nowrap transition-opacity hover:opacity-80"
                style={{ background: theme === 'dark' ? '#5ea6ff' : '#2b6df0' }}
              >
                로그인
              </Link>
            )}
          </div>

          {/* 모바일: 알림 + 테마 전환 */}
          <div className="flex items-center gap-1 md:hidden">
            {user?.id && <LazyComponents.NotificationCenter userId={user.id} />}
            <button
              onClick={toggleTheme}
              className={`p-1.5 rounded-lg transition-all duration-200 ${
                theme === 'light'
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-gray-800 text-yellow-400 hover:bg-gray-700'
              }`}
              aria-label="테마 전환"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </header>
    </div>
  )
}
